import React from 'react'
import { PenToolOverlay } from '@/components/app/editor/tools/pen-tool-overlay'
import { TextOnPathToolbar } from '@/components/app/editor/tools/text-on-path-toolbar'
import { CropZoomSlider } from '@/components/app/editor/tools/crop-zoom-slider'
import { applyCenterCanvasDefaults } from './helpers/apply-center-canvas-defaults'
import { resolveToolModes } from './helpers/resolve-tool-modes'
import { CanvasContentLayer } from './components/canvas-content-layer'
import { CanvasContextMenus } from './components/canvas-context-menus'
import { CanvasDropTargets } from './components/canvas-drop-targets'
import { CanvasRuler } from './components/canvas-ruler'
import { CheckpointDiagnosticsPanel } from './components/checkpoint-diagnostics-panel'
import { CropModeChrome } from './components/crop-mode-chrome'
import { DragCreateAdornments } from './components/drag-create-adornments'
import { GuidesLayer } from './components/guides-layer'
import { NodeEditModeChrome } from './components/node-edit-mode-chrome'
import { PhotoModeChrome } from './components/photo-mode-chrome'
import { RulerCorner } from './components/ruler-corner'
import { ScreenSpaceLayers } from './components/screen-space-layers'
import { SpreadDocument } from './components/spread-document'
import { SpreadOverlaysAboveShapes } from './components/spread-overlays-above-shapes'
import { H_RULER_THICKNESS_PX, V_RULER_THICKNESS_PX } from './constants/ruler-size'
import { useCanvasEditModeHandlers } from './hooks/groups/use-canvas-edit-mode-handlers'
import { useCanvasEditModes } from './hooks/groups/use-canvas-edit-modes'
import { useCanvasKeyboard } from './hooks/groups/use-canvas-keyboard'
import { useCanvasPointerGestures } from './hooks/groups/use-canvas-pointer-gestures'
import { useCanvasRulerGuides } from './hooks/groups/use-canvas-ruler-guides'
import { useCanvasFoundation } from './hooks/groups/use-canvas-foundation'
import { useCanvasSelectionCommands } from './hooks/groups/use-canvas-selection-commands'
import { useCanvasTextLinking } from './hooks/groups/use-canvas-text-linking'
import { useCanvasViewport } from './hooks/groups/use-canvas-viewport'
import { useCanvasZoomGestures } from './hooks/groups/use-canvas-zoom-gestures'
import { useCenterCanvasConfig } from './hooks/use-center-canvas-config'
import { useCenterCanvasHandle } from './hooks/use-center-canvas-handle'
import type { CenterCanvasHandle } from './types/center-canvas-handle'
import type { CenterCanvasProps } from './types/center-canvas-props'

/**
 * CenterCanvas with pinned rulers (top/left) synced to scroll.
 * Supports configurable spread dimensions and guide visibility while keeping
 * rulers and overlays synchronized to the same canvas coordinate system.
 */
const CenterCanvasInner = React.forwardRef<CenterCanvasHandle, CenterCanvasProps>(
  (rawProps, ref) => {
    const config = applyCenterCanvasDefaults(useCenterCanvasConfig(rawProps))

    const foundation = useCanvasFoundation(config)
    const viewport = useCanvasViewport(config, foundation)
    const toolModes = resolveToolModes(config)
    const linking = useCanvasTextLinking(config, foundation, toolModes)

    const editModes = useCanvasEditModes(config, foundation, toolModes)

    const selectionCommands = useCanvasSelectionCommands(
      config,
      foundation,
      viewport,
      linking,
      toolModes,
      editModes
    )

    const pointer = useCanvasPointerGestures(
      config,
      foundation,
      viewport,
      linking,
      toolModes,
      editModes,
      selectionCommands
    )

    const editModeHandlers = useCanvasEditModeHandlers(config, foundation, editModes)

    const zoomGestures = useCanvasZoomGestures(config, foundation, viewport, pointer)

    useCenterCanvasHandle({
      ref,
      internalShapeManagerRef: foundation.internalShapeManagerRef,
      fitWidth: zoomGestures.fitWidth,
      zoomToFitSelection: zoomGestures.zoomToFitSelection,
      centerSpreadForActualSize: viewport.centerSpreadForActualSize,
      refitFitSpread: viewport.refitFitSpread,
      zoomInFromToolbar: zoomGestures.zoomInFromToolbar,
      zoomOutFromToolbar: zoomGestures.zoomOutFromToolbar,
      centerViewportOnSelectionOrSpread: zoomGestures.centerViewportOnSelectionOrSpread,
      clearUnifiedSelectionWithHistory: editModes.clearUnifiedSelectionWithHistory,
      lockSelectedObjects: editModes.lockSelectedObjects,
      unlockSelectedObjects: editModes.unlockSelectedObjects,
      freezeSelectedObjects: editModes.freezeSelectedObjects,
      unfreezeSelectedObjects: editModes.unfreezeSelectedObjects,
      unfreezeAllObjects: editModes.unfreezeAllObjects,
      unlockAllObjects: editModes.unlockAllObjects,
      isShapeTextEditing: foundation.isShapeTextEditing,
      copySelectionToClipboard: selectionCommands.copySelectionToClipboard,
      cutSelectionForActiveMode: selectionCommands.cutSelectionForActiveMode,
      pasteFromClipboard: selectionCommands.pasteFromClipboard,
      hasPasteCandidate: selectionCommands.hasPasteCandidate,
      isTextEditSessionActive: selectionCommands.isTextEditSessionActive,
      deleteSelectionForActiveMode: selectionCommands.deleteSelectionForActiveMode,
      deleteSelectedObjects: selectionCommands.deleteSelectedObjects,
      onDeleteShapesHandled: config.onDeleteShapesHandled,
      selectAllForActiveTextEdit: selectionCommands.selectAllForActiveTextEdit,
      activeIsolationGroupId: editModes.activeIsolationGroupId,
      getCurrentSelectionSnapshots: selectionCommands.getCurrentSelectionSnapshots,
      penToolOverlayRef: editModeHandlers.penToolOverlayRef,
      setPenToolOverlayKey: editModeHandlers.setPenToolOverlayKey,
      nodeEditShapeIdRef: editModes.nodeEditShapeIdRef,
      setNodeEditShapeData: editModes.setNodeEditShapeData,
      tryEnterNodeEditMode: editModes.tryEnterNodeEditMode,
      exitNodeEditMode: editModes.exitNodeEditMode,
      nodeEditUndoFloorRef: editModes.nodeEditUndoFloorRef,
      cropShapeIdRef: editModes.cropShapeIdRef,
      setCropShapeData: editModes.setCropShapeData,
      tryEnterCropMode: editModes.tryEnterCropMode,
      exitCropMode: editModes.exitCropMode,
      cropUndoFloorRef: editModes.cropUndoFloorRef,
      enterTextOnPathModeById: editModes.enterTextOnPathModeById,
      commitAndExitTextOnPathMode: editModes.commitAndExitTextOnPathMode,
      resolveTextOnPathTargetId: editModes.resolveTextOnPathTargetId,
      getUndoStackSize: config.getUndoStackSize,
    })

    const rulerGuides = useCanvasRulerGuides(config, foundation, viewport, editModes)

    useCanvasKeyboard(config, foundation, linking, toolModes, editModes, selectionCommands, pointer)

    return (
      <div className="relative h-full w-full">
        {/* Text-linking mode announcements (WMPCM-9198). Keyed remount so the same message twice in
            a row still re-announces — mirrors the zoom/photo-placement regions in `home.tsx`. */}
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {linking.textLinkAnnouncement ? (
            <span key={linking.textLinkAriaKey}>{linking.textLinkAnnouncement}</span>
          ) : null}
        </div>
        {/* Dev-only diagnostics (no console spam) */}
        <CheckpointDiagnosticsPanel checkpointDiagnostics={config.checkpointDiagnostics} />
        {/* Pinned rulers */}
        <CanvasRuler
          axis="horizontal"
          ticks={rulerGuides.visibleRulerTicksH}
          stripMinPx={rulerGuides.rulerTicks.horizontalStripMinPx}
          stripRef={viewport.horizontalRulerStripRef}
          spreadOriginCanvasX={viewport.snappedSpreadOriginCanvasX}
          onRulerMouseDown={rulerGuides.handleRulerMouseDown}
        />
        <CanvasRuler
          axis="vertical"
          ticks={rulerGuides.visibleRulerTicksV}
          stripMinPx={rulerGuides.rulerTicks.verticalStripMinPx}
          stripRef={viewport.verticalRulerStripRef}
          spreadOriginCanvasX={viewport.snappedSpreadOriginCanvasX}
          onRulerMouseDown={rulerGuides.handleRulerMouseDown}
        />

        <RulerCorner />

        {/* Scrollable work area offset for rulers */}
        <div
          ref={foundation.scrollRef}
          data-testid="center-canvas-scroller"
          className="absolute right-0 bottom-0 overflow-auto bg-muted"
          style={{ left: V_RULER_THICKNESS_PX, top: H_RULER_THICKNESS_PX }}
          onPointerDown={pointer.handleScrollerPointerDown}
        >
          {/* Large pasteboard */}
          <div
            ref={foundation.pasteboardRef}
            data-testid="center-canvas-pasteboard"
            className="relative mx-auto"
            style={{
              width: viewport.pasteboardSize,
              height: viewport.pasteboardSize,
              cursor: pointer.canvasCursor,
            }}
            onPointerDownCapture={pointer.handleMarqueeZoomPointerDownCapture}
            onPointerDown={pointer.handlePasteboardPointerDown}
            onContextMenu={foundation.contextMenus.handleEmptyCanvasContextMenu}
            onPointerMove={pointer.handlePasteboardPointerMove}
            onPointerUp={pointer.handlePasteboardPointerUp}
            onPointerCancel={pointer.handlePasteboardPointerCancel}
          >
            {/* Center spread. Explicit left/top (not left-1/2/translate-1/2) because
                `transform: scale()` on the child below never affects this wrapper's own
                layout-box size — a percentage translate would anchor to the *unscaled*
                bleed box at every zoom level instead of the actual rendered (scaled) size,
                drifting the document off pasteboard-center as zoom changes.

                Centered on the ruler-zero point (spine X / top-of-trim Y), not on the raw
                bleed box: the vertical ruler's 0 sits near the *top* of the bleed box
                (offset only by `bleedMargin`), not its vertical middle, so centering on the
                bleed box left far more pasteboard below doc-Y=0 than above it — scrolling to
                the bottom read well past 15000pt while the top capped out well short of it
                (WMPCM-8911). Centering on ruler-zero instead gives an equal ± range on both
                rulers regardless of the document's aspect ratio. */}
            <div
              className="absolute"
              style={{
                left:
                  viewport.pasteboardSize / 2 -
                  viewport.snappedSpreadOriginCanvasX * viewport.scale,
                top:
                  viewport.pasteboardSize / 2 -
                  viewport.verticalRulerZeroAtCanvasY * viewport.scale,
              }}
            >
              <div
                className="origin-top-left"
                style={{
                  transform: `scale(${viewport.scale})`,
                  cursor: pointer.canvasCursor,
                }}
              >
                <div
                  ref={foundation.documentCanvasRef}
                  className="relative"
                  style={{ width: config.bleedWidth, height: config.bleedHeight }}
                  tabIndex={-1}
                  data-photo-placement-target="true"
                >
                  <CanvasDropTargets
                    getCanvasPointFromPointerEvent={viewport.getCanvasPointFromPointerEvent}
                    bleedWidth={config.bleedWidth}
                    bleedHeight={config.bleedHeight}
                    internalShapeManagerRef={foundation.internalShapeManagerRef}
                    linkedTextChains={config.linkedTextChains}
                    spreadBackgroundGeometry={viewport.spreadBackgroundGeometry}
                  />
                  <GuidesLayer
                    showGuides={config.showGuides}
                    isPanInteractionActive={foundation.isPanInteractionActive}
                    guides={pointer.visibleGuides}
                    selectedGuideIds={foundation.selectedGuideIds}
                    onGuideDragStart={rulerGuides.handleGuideDragStart}
                    rulerUnit={config.rulerUnit}
                    snappedSpreadOriginCanvasX={viewport.snappedSpreadOriginCanvasX}
                    verticalRulerZeroAtCanvasY={viewport.verticalRulerZeroAtCanvasY}
                    scale={viewport.scale}
                    singlePageHorizontalGuideSpan={viewport.singlePageHorizontalGuideSpan}
                    suspendPointerInteraction={foundation.isInlineTextEditing}
                    draggingGuide={rulerGuides.draggingGuide}
                  />
                  <SpreadDocument config={config} viewport={viewport} />
                  <DragCreateAdornments
                    dragPreviewShape={pointer.dragPreviewShape}
                    mirrorMovePreviewShapes={pointer.mirrorMovePreviewShapes}
                    dragCreate={pointer.dragCreate}
                    scale={viewport.scale}
                  />
                  <CanvasContentLayer
                    config={config}
                    foundation={foundation}
                    editModes={editModes}
                    linking={linking}
                    toolModes={toolModes}
                    viewport={viewport}
                    pointer={pointer}
                    selectionCommands={selectionCommands}
                    editModeHandlers={editModeHandlers}
                  />
                  {/* end unified content layer */}
                  {/* Pen Tool Overlay — above shapes and text, captures its own pointer events.
                      Suppressed when in node-edit mode (pen-edit sub-mode handled by NodeEditOverlay). */}
                  {toolModes.isInPenToolMode && !editModes.isNodeEditMode && (
                    <PenToolOverlay
                      key={editModeHandlers.penToolOverlayKey}
                      ref={editModeHandlers.penToolOverlayRef}
                      bleedWidth={config.bleedWidth}
                      bleedHeight={config.bleedHeight}
                      scale={viewport.scale}
                      defaultPointType={config.penToolDefaultPointType}
                      onPathCompleted={editModeHandlers.handlePenPathCompleted}
                      canvasScrollerRef={foundation.scrollRef}
                      onExitPenTool={config.onExitPenTool}
                      initialPoints={editModeHandlers.penToolInitialPoints}
                      skipShapeExitCheck={editModeHandlers.continuingShapeIdRef.current != null}
                      onDrawingStart={() => {
                        foundation.internalShapeManagerRef.current?.clearSelection()
                      }}
                      snapToGrid={config.snapToGrid}
                      gridSnapStep={viewport.documentGridSnapStepPt}
                      gridSnapLiveRects={viewport.gridSnapLiveRects}
                      smartGuidesEnabled={foundation.effectiveSmartGuides}
                      snapGuideDistance={config.snapGuideDistance}
                      getShapeBoundsForSmartGuides={editModeHandlers.getPenToolShapeBounds}
                      onSmartGuidesChange={foundation.setActiveSmartGuides}
                    />
                  )}
                  <NodeEditModeChrome
                    config={config}
                    foundation={foundation}
                    viewport={viewport}
                    editModes={editModes}
                    editModeHandlers={editModeHandlers}
                  />
                  <CropModeChrome
                    config={config}
                    foundation={foundation}
                    viewport={viewport}
                    editModes={editModes}
                    editModeHandlers={editModeHandlers}
                  />
                  {/* Text on a Path action bar — text card + control pill, floating beside the
                      object whose outline the text runs along (WMPCM-9514). */}
                  {editModes.textOnPathDraft && editModes.textOnPathShapeData && (
                    <TextOnPathToolbar
                      ref={editModes.textOnPathToolbarRef}
                      shape={editModes.textOnPathShapeData}
                      scale={viewport.scale}
                      canvasRef={foundation.documentCanvasRef}
                      draft={editModes.textOnPathDraft}
                      pathLength={editModes.textOnPathLength}
                      onTextChange={editModes.handleTextOnPathTextChange}
                      onStartDistanceChange={editModes.handleTextOnPathStartDistanceChange}
                      onConfigChange={editModes.handleTextOnPathConfigChange}
                      onDismiss={editModes.commitAndExitTextOnPathMode}
                    />
                  )}
                  {/* Crop Zoom Slider — floating panel below the shape being cropped; enlarges/
                      shrinks the photo inside the (fixed) crop frame. */}
                  {editModeHandlers.cropChromeShape && (
                    <CropZoomSlider
                      ref={editModes.cropZoomSliderRef}
                      shape={editModeHandlers.cropChromeShape}
                      scale={viewport.scale}
                      canvasRef={foundation.documentCanvasRef}
                      onScaleChange={editModeHandlers.handlePhotoPanChange}
                      onScaleCommit={editModeHandlers.handlePhotoPanCommit}
                    />
                  )}
                  <PhotoModeChrome
                    photoActionBar={selectionCommands.photoActionBar}
                    scale={viewport.scale}
                    documentCanvasRef={foundation.documentCanvasRef}
                    photoEffectsOpen={config.photoEffectsOpen}
                    onPhotoEffectsOpenChange={config.onPhotoEffectsOpenChange}
                  />
                  <SpreadOverlaysAboveShapes config={config} viewport={viewport} />
                  {/* Unified multi-select chrome (shapes + text): pass-through center; move strips; resize + rotate */}
                </div>
              </div>
              <ScreenSpaceLayers
                config={config}
                foundation={foundation}
                editModes={editModes}
                pointer={pointer}
                viewport={viewport}
                linking={linking}
                toolModes={toolModes}
              />
            </div>
          </div>
        </div>

        <CanvasContextMenus
          config={config}
          foundation={foundation}
          editModes={editModes}
          viewport={viewport}
          selectionCommands={selectionCommands}
        />
      </div>
    )
  }
)

CenterCanvasInner.displayName = 'CenterCanvas'

/**
 * Memoized: home passes only data and stable callbacks, so a home render that changes none of them
 * (most of its state) skips the canvas and every shape under it.
 */
const CenterCanvas = React.memo(CenterCanvasInner)

export default CenterCanvas
